import React from 'react';
import type { AppRecord } from '../../lib/types';
import { buildCompassHref, type CompassCopy, type CompassLocale } from '../i18n';
import { statusBadge } from '../lib/statusBadge';
import { buildCompassViewHref, deriveAppCategory, deriveAppTags, sortApps } from '../lib/catalogPresentation';

const badgeToneClasses: Record<string, string> = {
  blue: 'text-blue-400',
  gray: 'text-gray-400',
  green: 'text-green-400',
  red: 'text-red-400',
};

export function Gallery(props: {
  apps: AppRecord[];
  locale: CompassLocale;
  copy: CompassCopy;
  onLocaleChange: (locale: CompassLocale) => void;
  isIntegrated?: boolean;
}): React.JSX.Element {
  const { apps, locale } = props;
  const featuredApps = sortApps(apps, 'score').slice(0, 3);

  return (
    <main className="flex-grow pt-[100px] pb-12 px-4 md:px-10 max-w-[1280px] mx-auto w-full flex flex-col gap-16">
      <section className="flex flex-col items-center text-center justify-center min-h-[614px] gap-8 relative rounded-[32px] overflow-hidden">
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
        <div className="relative z-10 max-w-3xl flex flex-col gap-6 items-center">
          <h1 className="font-geist text-[56px] md:text-[64px] leading-[1.1] font-bold text-on-surface">
            Compass Gallery
          </h1>
          <p className="text-lg md:text-xl text-on-surface-variant max-w-2xl leading-relaxed">
            Explore AI-generated apps and the prompts behind them. / 探索 AI 生成的应用，以及它们背后的 Prompt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <a className="btn-primary-gradient px-8 py-3 rounded-full hover:scale-105 transition-transform duration-200 text-sm no-underline" href={buildCompassViewHref('gallery', locale)}>
              {locale === 'zh-CN' ? '浏览所有 App' : 'Browse All Apps'}
            </a>
            <a className="btn-secondary-cyan px-8 py-3 rounded-full hover:scale-105 transition-transform duration-200 text-sm no-underline" href={buildCompassViewHref('library', locale)}>
              {locale === 'zh-CN' ? '查看 Prompt 案例' : 'Explore Prompt Library'}
            </a>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-12">
        <div className="flex justify-between items-end gap-4">
           <h2 className="font-geist text-[32px] leading-[1.2] font-semibold text-on-surface">
             {locale === 'zh-CN' ? '精选应用' : 'Featured Apps'}
           </h2>
           <a className="text-sm font-semibold text-primary hover:text-white transition-colors no-underline" href={buildCompassViewHref('gallery', locale)}>
             {locale === 'zh-CN' ? '查看全部' : 'View All'}
           </a>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {featuredApps.map((app, index) => {
            const badge = statusBadge(app, locale);
            const category = deriveAppCategory(app);
            const badgeToneClass = badgeToneClasses[badge.color] ?? 'text-primary';
            const tags = deriveAppTags(app);
            
            return (
              <div 
                key={app.appId} 
                className="glass-card rounded-[16px] overflow-hidden flex flex-col group cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => { window.location.href = buildCompassHref(`/apps/${app.appId}`, locale); }}
              >
                <div className="h-48 relative overflow-hidden bg-surface-container-highest">
                  {app.screenshots[0]?.publishedUrl ? (
                    <img 
                      alt={app.name} 
                      className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-all duration-700 ease-out" 
                      src={app.screenshots[0].publishedUrl} 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                       <span className="material-symbols-outlined text-5xl opacity-10">dynamic_form</span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4 bg-cyan/20 border border-cyan text-cyan font-semibold text-[10px] px-3 py-1 rounded-full backdrop-blur-md uppercase tracking-widest z-10">
                    {category}
                  </div>
                </div>
                
                <div className="p-6 flex flex-col gap-4 flex-grow">
                  <div className="flex flex-col gap-3">
                    <h3 className="font-geist text-2xl font-semibold text-on-surface group-hover:text-primary transition-colors">
                      {app.name}
                    </h3>
                    <div className="flex gap-2 flex-wrap">
                      {tags.map((tag) => (
                        <span key={tag} className="bg-surface-bright text-on-surface-variant text-[11px] px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <p className="font-mono text-sm text-on-surface-variant line-clamp-3 leading-relaxed">
                    {app.summary}
                  </p>
                  
                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-outline-variant/10">
                    <button className="text-cyan text-xs font-semibold uppercase tracking-widest flex items-center gap-1" type="button">
                      {locale === 'zh-CN' ? '查看详情' : 'View Details'}
                      <span className="material-symbols-outlined text-[16px] group-hover/btn:translate-x-1 transition-transform">arrow_forward</span>
                    </button>
                    <span className={`text-[10px] font-semibold uppercase tracking-widest ${badgeToneClass}`}>{badge.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
